import React, { useEffect, useMemo, useState, useRef } from "react";
import { Modal, SurveyTab } from "./components";
import {
  useConfiguration,
  SurveyConfig,
  Trigger
} from "./configuration";
import { DefaultTriggerResolver, TriggerResolver } from "./triggers";
import { Survey } from "./survey";

export interface SurveysProps {
  path: string;
  resolver?: TriggerResolver;
}

export default function Surveys({ path, resolver }: SurveysProps) {
  const [open, setOpen] = useState(false);
  const { surveys, tab } = useConfiguration();
  const [survey, setSurvey] = useState<SurveyConfig | undefined>();
  const surveyRef = useRef<SurveyConfig | undefined>();
  const triggerResolver = useRef<TriggerResolver>(
    resolver ?? new DefaultTriggerResolver(surveys)
  );

  useEffect(() => {
    if (!open) {
      surveyRef.current = undefined;
    }
  }, [open]);

  const setResolvedSurvey = (resolvedSurvey: SurveyConfig) => {
    setSurvey(resolvedSurvey);
    surveyRef.current = resolvedSurvey;
    setOpen(true);
    triggerResolver.current.setShown(resolvedSurvey);
  };

  const resolveButtonTrigger = () => {
    const resolvedSurvey = triggerResolver.current.resolve(Trigger.BUTTON, {
      path: path,
    });
    return resolvedSurvey;
  };

  const handleOpen = () => {
    if (!surveyRef.current) {
      const resolvedSurvey = resolveButtonTrigger();
      if (resolvedSurvey) {
        setResolvedSurvey(resolvedSurvey);
      }
    }
  };

  useEffect(() => {
    const resolvedSurvey = triggerResolver.current.resolve(Trigger.LOCATION, {
      path: path,
    });
    if (resolvedSurvey) {
      const timer = setTimeout(() => {
        if (!surveyRef.current) {
          setResolvedSurvey(resolvedSurvey);
        }
      }, (resolvedSurvey.time ?? 0) * 1000);
      return () => {
        clearTimeout(timer);
      }
    }
  }, [path, triggerResolver]);

  useEffect(() => {
    const handleMouseOut = (e) => {
      const fromTop = e.clientY;
      if (
        !surveyRef.current &&
        !e.toElement &&
        !e.relatedTarget &&
        fromTop < 0
      ) {
        const resolvedSurvey = triggerResolver.current.resolve(
          Trigger.MOUSEOUT,
          {
            path: path,
          }
        );
        if (resolvedSurvey) {
          setResolvedSurvey(resolvedSurvey);
        }
      }
    };
    document.addEventListener("mouseout", handleMouseOut);
    return () => {
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [path, triggerResolver]);

  const isTabEnabled = useMemo(() => {
    return (
      !open &&
      tab?.show &&
      (!tab.path || new RegExp(tab.path).test(path)) &&
      triggerResolver.current.resolve(Trigger.BUTTON, { path: path })
    );
  }, [open, path]);

  return (
    <>
      {isTabEnabled && <SurveyTab onClick={handleOpen} />}
      <Modal open={open} onClose={() => setOpen(false)}>
        {survey && (
          <div className="w-full max-w-sm mx-auto ">
            <Survey.Form
              path={path}
              survey={survey}
              callback={() => setOpen(false)}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
